import { Howl, Howler } from 'howler'
import correctAnswerSfx from '@/assets/audio/sfx/correct-answer.mp3'
import incorrectAnswerSfx from '@/assets/audio/sfx/incorrect-answer.mp3'
import timeoutAnswerSfx from '@/assets/audio/sfx/times-up.mp3'
import clickSubmitSfx from '@/assets/audio/sfx/click-submit.mp3'
import pointInscreaseSfx from '@/assets/audio/sfx/scoring.mp3'
import streakSfx from '@/assets/audio/sfx/streak.mp3'

// Enable global settings for best iOS compatibility
Howler.autoUnlock = true

// ============================================
// SOUND EFFECT SOURCES - Only paths, lazy load
// ============================================
const SOUND_EFFECT_SOURCES = {
  CORRECT_ANSWER: correctAnswerSfx,
  INCORRECT_ANSWER: incorrectAnswerSfx,
  TIMEOUT_ANSWER: timeoutAnswerSfx,
  POINT_INCREASE: pointInscreaseSfx,
  CLICK_SUBMIT: clickSubmitSfx,
  STREAK: streakSfx,
}

// ============================================
// CACHE - Store created Howl instances
// ============================================
const soundEffectCache = {}

// ============================================
// FACTORY FUNCTIONS - Lazy load with cache
// ============================================

/**
 * Get sound effect by key - lazy load and cache
 * @param {string} key - Sound effect key (e.g., 'CORRECT_ANSWER')
 * @returns {Howl|null} - Howl instance or null if key not found
 */
export const getSoundEffect = (key) => {
  if (!soundEffectCache[key]) {
    const src = SOUND_EFFECT_SOURCES[key]
    if (!src) return null
    soundEffectCache[key] = new Howl({
      src: [src],
      preload: true,
      html5: false, // Use Web Audio for better latency and unlocking
    })
  }
  return soundEffectCache[key]
}

// Export keys for type safety
export const SOUND_EFFECT_KEYS = Object.keys(SOUND_EFFECT_SOURCES)

// ============================================
// HELPER FUNCTIONS
// ============================================

const playSound = (sound, debugName = 'Unknown') => {
  if (!sound) return null

  // Howler handles volume and muted state via its own API or global Howler object.
  // By default we play at full volume.
  sound.volume(1)

  // Stop and play to ensure it restarts from beginning (like currentTime = 0)
  sound.stop()
  const id = sound.play()

  if (!id) {
    console.warn(`[Audio] Playback failed/blocked (${debugName})`)
  }

  return id
}

const stopSound = (sound) => {
  if (sound) {
    sound.stop()
  }
}

/**
 * Howler handles unlocking on the first user interaction automatically.
 * We keep this as a no-op action for backward compatibility/explicit triggers.
 */
const warmUpAudio = () => {
  // Howler.js handles this via autoUnlock: true.
  // We can explicitly trigger Howler's unlock if needed.
  if (Howler.ctx && Howler.ctx.state === 'suspended') {
    Howler.ctx.resume()
  }
}

// ============================================
// STATE
// ============================================
const _state = {
  activeSounds: [], // Active sound effects (IDs or instances)
}

// ============================================
// GETTERS
// ============================================
const _getters = {

}

// ============================================
// MUTATIONS
// ============================================
const mutations = {
  ADD_ACTIVE_SOUND(state, sound) {
    if (!state.activeSounds.includes(sound)) {
      state.activeSounds.push(sound)
    }
  },
  REMOVE_ACTIVE_SOUND(state, sound) {
    const index = state.activeSounds.indexOf(sound)
    if (index > -1) {
      state.activeSounds.splice(index, 1)
    }
  },
  CLEAR_ALL_ACTIVE_SOUNDS(state) {
    // eslint-disable-next-line no-param-reassign
    state.activeSounds = []
  },
}

// ============================================
// ACTIONS
// ============================================
const actions = {
  /**
   * Play sound effect by key (lazy load)
   * @param {string} key - Sound effect key (e.g., 'CONFETTI')
   * @returns {number|null} - Sound ID or null
   */
  async playSoundEffectAction({ commit, rootGetters, rootState }, key) {
    if (!rootGetters['featureToggles/canUseNewSoundEffects']) return null
    if (rootState.presentation?.isEnableSoundEffectsForAudience === false) return null
    const sound = getSoundEffect(key)
    if (!sound) return null

    commit('ADD_ACTIVE_SOUND', sound)
    sound.once('end', () => {
      commit('REMOVE_ACTIVE_SOUND', sound)
    })

    return playSound(sound, key)
  },

  /**
   * Stop sound effect by key
   * @param {string} key - Sound effect key
   */
  stopSoundEffectAction({ commit }, key) {
    const sound = getSoundEffect(key)
    if (!sound) return

    stopSound(sound)
    commit('REMOVE_ACTIVE_SOUND', sound)
  },

  /**
   * Play sound effect instance directly (for dynamic sounds)
   * @param {Howl} sound - Howl instance
   */
  playSoundInstanceAction({ state, commit }, sound) {
    if (!sound) return
    if (state.activeSounds.includes(sound)) return

    commit('ADD_ACTIVE_SOUND', sound)
    sound.once('end', () => {
      commit('REMOVE_ACTIVE_SOUND', sound)
    })
    stopSound(sound)
    playSound(sound)
  },

  /**
   * Stop sound effect instance directly
   * @param {Howl} sound - Howl instance
   */
  stopSoundInstanceAction({ commit }, sound) {
    if (!sound) return
    stopSound(sound)
    commit('REMOVE_ACTIVE_SOUND', sound)
  },

  registerSoundAction({ commit }, sound) {
    commit('ADD_ACTIVE_SOUND', sound)
  },

  unregisterSoundAction({ commit }, sound) {
    stopSound(sound)
    commit('REMOVE_ACTIVE_SOUND', sound)
  },

  preloadSoundEffectAction() {
    // Preload all sound effects
    SOUND_EFFECT_KEYS.forEach((key) => {
      getSoundEffect(key)
    })
  },

  warmUpAudioAction() {
    warmUpAudio()
  },
}

const ACTION_HANDLERS = {
  BUTTON_CLICK: (store, action) => {
    const sfxKey = action.payload
    store.dispatch('audienceAudio/playSoundEffectAction', sfxKey)
  },
  'QUIZ/ANSWER_RESULT': (store, action) => {
    const { result, animationDelay } = action.payload
    if (result.timeout) {
      store.dispatch('audienceAudio/playSoundEffectAction', 'TIMEOUT_ANSWER')
      return
    }
    const sfxKey = result.correct ? 'CORRECT_ANSWER' : 'INCORRECT_ANSWER'
    store.dispatch('audienceAudio/playSoundEffectAction', sfxKey)
    if (result.correct) {
      setTimeout(() => {
        store.dispatch('audienceAudio/playSoundEffectAction', 'POINT_INCREASE')
      }, animationDelay)
    }
  },
}

const audioPlugin = (store) => {
  store.subscribeAction((action) => {
    if (!store.getters['featureToggles/canUseNewSoundEffects']) return

    const handler = ACTION_HANDLERS[action.type]
    if (handler) {
      handler(store, action)
    }

    if (action.type === 'setPresentationAction') {
      store.dispatch('audienceAudio/preloadSoundEffectAction')
    }
  })
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  mutations,
  actions,
  plugins: [audioPlugin],
}
